import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-514ab203"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "connection-setup-prompt"
}
const _hoisted_2 = {
  key: 1,
  class: "connection-setup-error"
}
const _hoisted_3 = {
  key: 3,
  class: "connection-setup-loading"
}

import { computed, defineAsyncComponent, nextTick, ref, watch } from "vue";
import { ElDialog } from "element-plus";
import i18n from "@/core/i18n/i18n";
import {
  getConnection,
  getOrganizationsConnectorList,
} from "@/connection/services/connection.service";
import type { StepConnection } from "@/connection/services/connection.service";

interface ConnectionSetupRequest {
  mode?: string;
  connector_spec_id?: string;
  connector_name?: string;
  suggested_name?: string;
  auth_schemes?: string[];
  prefill?: Record<string, string>;
  prompt?: string;
  // Edit mode only: the connection to load into the form.
  connection_id?: string;
  connection_name?: string;
}
/* eslint-enable camelcase */


export default /*#__PURE__*/_defineComponent({
  __name: 'ConnectionSetupDialog',
  props: {
    modelValue: { type: Boolean },
    request: {},
    selectedOrgId: {}
  },
  emits: ["update:modelValue", "created"],
  setup(__props: any, { emit: __emit }) {

/**
 * Renders the standard OneTeg connection form in a dialog, in response to the
 * wizard's `connection_setup` interrupt.
 *
 * The form itself performs the POST to the backend — credentials (auth fields and
 * connector settings alike, either of which may be secret) go straight from this
 * form to Java and never pass through the wizard or the LLM.
 */
const ConnectionForm = defineAsyncComponent(
  () => import("@/connection/components/ConnectionForm.vue")
);

/*
 * Snake_case mirrors the wizard's interrupt payload exactly — it is a wire contract
 * produced by the Python service, not a local shape, so it is not renamed here.
 */
/* eslint-disable camelcase */
const props = __props;

const emit = __emit;

const { t } = i18n.global;
const connectorList = ref<any[]>([]);
const loadError = ref("");
// Most recent connection state from the form's draft-sync event. After a successful
// save the form replaces this with the persisted record, so it carries the new id.
const latestConnection = ref<any>(null);
// Edit mode: the connection fetched from the backend, used to seed the form.
const existingConnection = ref<any>(null);

const dialogTitle = computed(() => {
  const base = isEdit.value
    ? t("TeggyAssistant.ConnectionEditTitle")
    : t("TeggyAssistant.ConnectionSetupTitle");
  const subject = isEdit.value
    ? props.request?.connection_name
    : props.request?.connector_name;
  return subject ? `${base} — ${subject}` : base;
});

// In edit mode the form must not mount until the existing connection has loaded,
// otherwise it would seed itself blank and a save would clear the stored values.
const formReady = computed(
  () =>
    Boolean(connectorList.value.length && props.request?.connector_spec_id) &&
    (!isEdit.value || Boolean(existingConnection.value))
);

const formKey = computed(
  () =>
    `${props.request?.connection_id ?? "new"}-${
      props.request?.connector_spec_id ?? ""
    }`
);

const isEdit = computed(() => props.request?.mode === "connection_edit");

/**
 * In edit mode the form is seeded with the connection fetched from the backend, so
 * `encryptedFields` and `active` are echoed back on save — the server needs both to
 * preserve secrets the user did not retype. Otherwise a blank shape-complete seed is
 * used (see below).
 */
const seedConnection = computed(() =>
  isEdit.value ? existingConnection.value : blankConnection.value
);

/**
 * ConnectionForm replaces its entire internal model with `oldConnection` when one is
 * given, so the seed has to be shape-complete — a partial object would break its
 * field access. This mirrors the component's own defaultConnection() with the
 * wizard's suggested name applied.
 */
const blankConnection = computed(() => ({
  name: props.request?.suggested_name,
  authentication: {
    authenticationType: undefined,
    oauth: {
      clientId: "",
      clientSecret: "",
      callbackUrl: "",
      endPoint: undefined,
      accessToken: undefined,
      refreshToken: undefined,
      tokenExpiry: undefined,
    },
    basicAuth: {
      endPoint: undefined,
      username: undefined,
      password: undefined,
    },
    formAuth: {
      form: undefined,
      authentication: undefined,
      endPoint: undefined,
    },
    tokenAuth: { authToken: undefined, endPoint: undefined },
    apiKeyAuth: { apiKey: undefined, nameInHeader: undefined },
  },
  connector: {
    active: true,
    camelComponentName: undefined,
    connectorSpecId: props.request?.connector_spec_id,
    createdAt: undefined,
    createdBy: { id: undefined },
    connectorType: undefined,
    description: undefined,
    hasConsumer: false,
    id: undefined,
    modifiedBy: { id: undefined },
    name: undefined,
    organisation: {
      id: undefined,
      createdBy: { id: undefined },
      modifiedBy: { id: undefined },
    },
    pricingType: undefined,
    updatedAt: undefined,
  },
  additionalSettings: undefined,
  authParams: undefined,
  // Primitive booleans server-side: omitting them persists false, which would
  // silently create connections with SSL validation disabled.
  sslValidation: true,
  followRedirects: true,
}));

/**
 * ConnectionForm is built around a flow step; the wizard chat has none, so a
 * synthetic one is supplied. `selectedName` must stay empty — the component uses a
 * non-empty value to disable its inputs.
 */
const stepConnection = computed<StepConnection>(() => ({
  stepId: `wizard-${props.request?.connector_spec_id ?? "connection"}`,
  stepName: props.request?.connector_name ?? "",
  specId: props.request?.connector_spec_id ?? "",
  requiresCreate: true,
  connectionId: "",
  connection: null,
  addNew: true,
  nameExists: false,
  uniqueConnection: false,
  selectedName: "",
}));

async function loadConnectors(): Promise<void> {
  loadError.value = "";
  try {
    const list = await getOrganizationsConnectorList();
    connectorList.value = Array.isArray(list) ? list : [];
    if (!connectorList.value.length) {
      loadError.value = t("TeggyAssistant.ConnectionSetupUnavailable");
    }
  } catch (error) {
    connectorList.value = [];
    loadError.value = t("TeggyAssistant.ConnectionSetupUnavailable");
  }
}

/**
 * Load the connection being edited. The backend strips encrypted values (they come
 * back as null) and returns `encryptedFields`, which the form echoes on save so the
 * server can restore anything the user left untouched.
 */
async function loadExistingConnection(): Promise<void> {
  const connectionId = props.request?.connection_id;
  if (!connectionId) {
    return;
  }
  try {
    const connection = await getConnection(connectionId);
    if (connection) {
      existingConnection.value = connection;
    } else {
      loadError.value = t("TeggyAssistant.ConnectionEditUnavailable");
    }
  } catch (error) {
    loadError.value = t("TeggyAssistant.ConnectionEditUnavailable");
  }
}

watch(
  () => [
    props.modelValue,
    props.request?.connector_spec_id,
    props.request?.connection_id,
  ],
  ([visible]) => {
    if (!visible) {
      return;
    }
    latestConnection.value = null;
    existingConnection.value = null;
    loadConnectors();
    if (isEdit.value) {
      loadExistingConnection();
    }
  },
  { immediate: true }
);

function onVisibilityChange(value: boolean): void {
  emit("update:modelValue", value);
}

// Name uniqueness is enforced by the backend, which rejects duplicates with a
// dedicated error code — no client-side pre-check is needed here.
function onCheckName(): void {
  /* no-op */
}

/**
 * ConnectionForm's `update` event is a draft sync, not a save: it fires from deep
 * watchers on every edit (and immediately on mount). Only the connection it carries
 * is kept here — completion is signalled separately.
 */
function onDraftChange(_index: number, draft: any): void {
  latestConnection.value = draft?.connection ?? draft ?? null;
}

/**
 * `update-connection-valid` is emitted only after a successful save — directly for
 * non-OAuth connections, and after the OAuth browser flow completes. That makes it
 * the point at which the connection genuinely exists and the wizard can continue.
 */
async function onConnectionReady(ready: boolean): Promise<void> {
  if (!ready) {
    return;
  }
  // ConnectionForm assigns the saved record to its model and then emits this signal
  // synchronously in the same tick, while the deep watcher that reports that record
  // (carrying the new id) flushes on a microtask. Wait for it before reading the id.
  await nextTick();
  const connection = latestConnection.value;
  // This signal is only emitted after a successful save, so always close. The id is
  // best-effort for the resume message — the wizard re-reads connections regardless.
  emit("created", {
    connectionId: connection?.id,
    name: connection?.name,
  });
  emit("update:modelValue", false);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(ElDialog), {
      "model-value": _ctx.modelValue,
      title: dialogTitle.value,
      width: "760px",
      "custom-class": "connection-setup-dialog",
      "append-to-body": "",
      "destroy-on-close": "",
      "close-on-click-modal": false,
      "onUpdate:modelValue": onVisibilityChange
    }, {
      default: _withCtx(() => [
        (_ctx.request?.prompt)
          ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_ctx.request.prompt), 1))
          : _createCommentVNode("", true),
        (loadError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(loadError.value), 1))
          : (formReady.value)
            ? (_openBlock(), _createBlock(_unref(ConnectionForm), {
                key: formKey.value,
                index: 0,
                "spec-id": _ctx.request.connector_spec_id,
                connectors: connectorList.value,
                "old-connection": seedConnection.value,
                "step-connection": stepConnection.value,
                "name-exist": false,
                selectedOrgId: _ctx.selectedOrgId,
                "connection-id": _ctx.request.connection_id,
                onUpdate: onDraftChange,
                onCheckName: onCheckName,
                onUpdateConnectionValid: onConnectionReady
              }, null, 8, ["spec-id", "connectors", "old-connection", "step-connection", "selectedOrgId", "connection-id"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(t)("Common.Loading")), 1))
      ]),
      _: 1
    }, 8, ["model-value", "title"])
  ]))
}
}

})